import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, 
  DollarSign, 
  Plus, 
  Edit, 
  Trash2, 
  Calendar,
  CreditCard,
  FileText,
  User,
  MoreVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { apiService } from '../services/api';
import { InvoiceDto, PaymentDto } from '../types';
import { formatTimestamp } from '../utils/formatters';
import { useCompanyTimeZone } from '../hooks/useCompanyTimeZone';
import { useAuth } from '../contexts/AuthContext';
import AddPaymentModal from '../components/payment/AddPaymentModal';
import EditPaymentModal from '../components/payment/EditPaymentModal';
import ConfirmDialog from '../components/modals/ConfirmDialog';

const PaymentsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('invoiceId');
  const { companyTimeZone } = useCompanyTimeZone();
  const { user } = useAuth();

  console.log('PaymentsPage: Component loaded with invoiceId:', invoiceId);

  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showEditPayment, setShowEditPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentDto | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<PaymentDto | null>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[id^="payment-"][id$="-dropdown"]') && !target.closest('button[onClick*="dropdown"]')) {
        document.querySelectorAll('[id^="payment-"][id$="-dropdown"]').forEach(el => {
          el.classList.add('hidden');
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch invoice details
  const { data: invoice, isLoading: invoiceLoading, error: invoiceError } = useQuery({
    queryKey: ['invoices', invoiceId],
    queryFn: () => {
      console.log('Fetching invoice with ID:', invoiceId, 'as number:', Number(invoiceId));
      return apiService.getInvoiceById(Number(invoiceId));
    },
    enabled: !!invoiceId,
    onError: (error) => {
      console.error('Error fetching invoice:', error);
      toast.error('Failed to load invoice details');
    }
  });

  // Fetch payments for this invoice
  const { data: payments, isLoading: paymentsLoading, error: paymentsError } = useQuery({
    queryKey: ['payments', invoiceId],
    queryFn: async () => {
      console.log('Fetching payments for invoice ID:', invoiceId);
      const result = await apiService.getPaymentsByInvoice(Number(invoiceId));
      console.log('Payments result:', result);
      console.log('Payments result type:', typeof result);
      
      // Handle the wrapped response format
      const paymentArray = result?.value || result;
      console.log('Payment array:', paymentArray);
      console.log('Is payments an array?', Array.isArray(paymentArray));
      console.log('Payments length:', paymentArray?.length);
      
      return paymentArray;
    },
    enabled: !!invoiceId,
    onError: (error) => {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payment history');
    }
  });

  console.log('PaymentsPage: payments state:', { payments, paymentsLoading, paymentsError });

  // Delete payment mutation
  const deletePaymentMutation = useMutation({
    mutationFn: (id: number) => apiService.deletePayment(id),
    onSuccess: () => {
      toast.success('Payment deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['payments', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
    },
    onError: (error: any) => {
      toast.error('Failed to delete payment');
      console.error('Error deleting payment:', error);
    }
  });

  const handleDeletePayment = (payment: PaymentDto) => {
    setPaymentToDelete(payment);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePayment = () => {
    if (paymentToDelete) {
      deletePaymentMutation.mutate(paymentToDelete.id);
      setShowDeleteConfirm(false);
      setPaymentToDelete(null);
    }
  };

  const handleEditPayment = (payment: PaymentDto) => {
    setSelectedPayment(payment);
    setShowEditPayment(true);
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case 'cash':
        return <DollarSign className="h-4 w-4" />;
      case 'check':
        return <FileText className="h-4 w-4" />;
      case 'credit card':
        return <CreditCard className="h-4 w-4" />;
      case 'bank transfer':
        return <CreditCard className="h-4 w-4" />;
      default:
        return <DollarSign className="h-4 w-4" />;
    }
  };

  const getPaymentMethodColor = (method: string) => {
    switch (method.toLowerCase()) {
      case 'cash':
        return 'text-green-600 dark:text-green-400';
      case 'check':
        return 'text-blue-600 dark:text-blue-400';
      case 'credit card':
        return 'text-purple-600 dark:text-purple-400';
      case 'bank transfer':
        return 'text-indigo-600 dark:text-indigo-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (invoiceLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-6"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
            <div className="space-y-4">
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (invoiceError || !invoice) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Invoice Not Found
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              The invoice you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <Button onClick={() => navigate('/invoices')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Invoices
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const totalPaid = Array.isArray(payments) ? payments.reduce((sum, payment) => sum + payment.amount, 0) : 0;
  const remainingBalance = invoice.total - totalPaid;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => navigate('/invoices')}
                className="flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Invoices
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Payment Management
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Invoice {invoice.invoiceNumber} • {invoice.clientName}
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowAddPayment(true)}
              className="bg-accent-color-600 hover:bg-accent-color-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Record Payment
            </Button>
          </div>
        </div>

        {/* Invoice Summary */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Invoice Summary
            </h2>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${invoice.total.toFixed(2)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Invoice Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  ${totalPaid.toFixed(2)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Paid</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${
                  remainingBalance > 0 
                    ? 'text-red-600 dark:text-red-400' 
                    : 'text-green-600 dark:text-green-400'
                }`}>
                  ${remainingBalance.toFixed(2)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Remaining Balance</div>
              </div>
              <div className="text-center">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  remainingBalance === 0 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : remainingBalance < invoice.total
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  {remainingBalance === 0 ? 'Paid in Full' : remainingBalance < invoice.total ? 'Partially Paid' : 'Unpaid'}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Status</div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Payment History
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              All payments recorded for this invoice
            </p>
          </div>

          <div className="px-6 py-4">
            {paymentsLoading ? (
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
                ))}
              </div>
            ) : paymentsError ? (
              <div className="text-center py-8">
                <div className="text-red-600 dark:text-red-400 mb-4">
                  Error loading payment history
                </div>
                <Button onClick={() => window.location.reload()}>
                  Try Again
                </Button>
              </div>
            ) : !Array.isArray(payments) || payments.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No payments recorded
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  This invoice hasn't received any payments yet.
                </p>
                <Button
                  onClick={() => setShowAddPayment(true)}
                  className="bg-accent-color-600 hover:bg-accent-color-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Record First Payment
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {payments.map((payment: PaymentDto) => (
                  <div key={payment.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`p-3 rounded-full bg-gray-100 dark:bg-gray-700 ${getPaymentMethodColor(payment.paymentMethod)}`}>
                          {getPaymentMethodIcon(payment.paymentMethod)}
                        </div>
                        <div>
                          <div className="text-xl font-semibold text-gray-900 dark:text-white">
                            ${payment.amount.toFixed(2)}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {payment.paymentMethod}
                            {payment.referenceNumber && ` • ${payment.referenceNumber}`}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {formatTimestamp(payment.paymentDate, companyTimeZone || undefined).date} at {formatTimestamp(payment.paymentDate, companyTimeZone || undefined).time}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="text-right">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            Recorded by {payment.createdByName}
                          </div>
                          {payment.notes && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-xs truncate">
                              {payment.notes}
                            </div>
                          )}
                        </div>
                        <div className="relative">
                          <button
                            onClick={() => {
                              // Toggle dropdown or show actions
                              const dropdown = document.getElementById(`payment-${payment.id}-dropdown`);
                              if (dropdown) {
                                dropdown.classList.toggle('hidden');
                              }
                            }}
                            className="inline-flex items-center p-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-color-500 dark:focus:ring-offset-gray-900"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          <div
                            id={`payment-${payment.id}-dropdown`}
                            className="hidden absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-600"
                          >
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  handleEditPayment(payment);
                                  const dropdown = document.getElementById(`payment-${payment.id}-dropdown`);
                                  if (dropdown) dropdown.classList.add('hidden');
                                }}
                                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <Edit className="h-4 w-4 mr-3" />
                                Edit Payment
                              </button>
                              {(user?.roleName === 'Admin' || user?.roleName === 'SystemAdmin') && (
                                <button
                                  onClick={() => {
                                    handleDeletePayment(payment);
                                    const dropdown = document.getElementById(`payment-${payment.id}-dropdown`);
                                    if (dropdown) dropdown.classList.add('hidden');
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                                >
                                  <Trash2 className="h-4 w-4 mr-3" />
                                  Delete Payment
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {payment.notes && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <strong>Notes:</strong> {payment.notes}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        {invoice && (
          <AddPaymentModal
            isOpen={showAddPayment}
            onClose={() => setShowAddPayment(false)}
            invoice={invoice}
          />
        )}

        {selectedPayment && (
          <EditPaymentModal
            isOpen={showEditPayment}
            onClose={() => {
              setShowEditPayment(false);
              setSelectedPayment(null);
            }}
            payment={selectedPayment}
          />
        )}
      </div>
      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPaymentToDelete(null);
        }}
        onConfirm={confirmDeletePayment}
        title="Delete Payment"
        message={`Are you sure you want to delete this payment of $${paymentToDelete?.amount.toFixed(2)}? This action cannot be undone.`}
        confirmText="Delete Payment"
        cancelText="Cancel"
        isLoading={deletePaymentMutation.isPending}
        variant="danger"
      />
    </div>
  );
};

export default PaymentsPage;
